import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getMenuEditorOverview } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DollarSign, Bot, Coins, Upload } from "lucide-react";

function ImportBadge({ status }: { status: "ok" | "failed" | null }) {
  if (status === "ok") return <Badge variant="success">OK</Badge>;
  if (status === "failed") return <Badge variant="destructive">Failed</Badge>;
  return <span className="text-muted-foreground">-</span>;
}

export function MenuEditorOverview() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["menu-editor-overview"],
    queryFn: getMenuEditorOverview,
  });

  if (isLoading) {
    return <div className="p-8">Loading...</div>;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
        Error: {error.message}
      </div>
    );
  }

  const {
    revenue_this_month,
    other_fees_this_month,
    ai_chat_cost_month_usd,
    ai_import_cost_month_usd,
    credits_outstanding,
    credits_liability_usd,
    imports_30d_ok,
    imports_30d_failed,
    active_accounts_30d,
    top_accounts,
    new_accounts,
    recent_payments,
  } = data!;

  const feeLines = [
    ...revenue_this_month
      .filter((r) => parseFloat(r.fees) > 0)
      .map((r) => `-${formatCurrency(r.fees, r.currency)}`),
    ...other_fees_this_month.map(
      (f) => `-${formatCurrency(f.amount, f.currency)}`,
    ),
  ];

  const aiCostsTotal = ai_chat_cost_month_usd + ai_import_cost_month_usd;
  const onlyRevenueCurrency =
    revenue_this_month.length === 1 ? revenue_this_month[0] : undefined;
  const singleEurNet =
    onlyRevenueCurrency?.currency === "EUR"
      ? parseFloat(onlyRevenueCurrency.net)
      : null;
  const marginPct =
    singleEurNet !== null && singleEurNet > 0
      ? ((singleEurNet - aiCostsTotal) / singleEurNet) * 100
      : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Menu Editor</h1>
        <p className="text-sm text-muted-foreground">
          Revenue, AI costs and onboarding at a glance
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Net revenue (month)
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {revenue_this_month.length === 0
                ? formatCurrency(0)
                : revenue_this_month
                    .map((r) => formatCurrency(r.net, r.currency))
                    .join(" + ")}
            </div>
            <p className="text-xs text-muted-foreground">
              {revenue_this_month.length > 0
                ? revenue_this_month
                    .map((r) => `gross ${formatCurrency(r.gross, r.currency)}`)
                    .join(" · ")
                : "no payments yet"}
              {feeLines.length > 0 && ` · fees ${feeLines.join(" ")}`}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              AI costs (month)
            </CardTitle>
            <Bot className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${aiCostsTotal.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              chat ${ai_chat_cost_month_usd.toFixed(2)} + import $
              {ai_import_cost_month_usd.toFixed(2)}
              {marginPct !== null &&
                ` · margin ≈ ${marginPct.toFixed(0)}% of net`}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Credits outstanding
            </CardTitle>
            <Coins className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{credits_outstanding}</div>
            {credits_liability_usd !== null && (
              <p className="text-xs text-muted-foreground">
                ≈ ${credits_liability_usd.toFixed(2)} future AI cost
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Menu imports (30d)
            </CardTitle>
            <Upload className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {imports_30d_ok} OK / {imports_30d_failed} failed
            </div>
            <p className="text-xs text-muted-foreground">
              {active_accounts_30d} active accounts
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top accounts */}
      <Card>
        <CardHeader>
          <CardTitle>Top accounts (30d)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {top_accounts.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No activity yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Import</TableHead>
                  <TableHead className="text-right">Credits</TableHead>
                  <TableHead className="text-right">Conversations</TableHead>
                  <TableHead className="text-right">AI cost</TableHead>
                  <TableHead className="text-right">Lifetime spend</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {top_accounts.map((account, idx) => (
                  <TableRow key={`${account.organization_id}-${idx}`}>
                    <TableCell>
                      <Link
                        to={`/organizations/${account.organization_id}`}
                        className="font-medium hover:underline"
                      >
                        {account.account_name || account.owner_email}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        {account.owner_email}
                      </p>
                    </TableCell>
                    <TableCell>
                      <ImportBadge status={account.import_status} />
                    </TableCell>
                    <TableCell className="text-right">
                      {account.credits_balance}
                    </TableCell>
                    <TableCell className="text-right">
                      {account.conversations_30d}
                    </TableCell>
                    <TableCell className="text-right">
                      ${account.ai_cost_30d_usd.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {account.lifetime_spend.length === 0
                        ? "-"
                        : account.lifetime_spend
                            .map((s) => formatCurrency(s.amount, s.currency))
                            .join(" + ")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New accounts */}
      <Card>
        <CardHeader>
          <CardTitle>New accounts (14d)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {new_accounts.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              No new signups yet
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Signup</TableHead>
                  <TableHead>Import</TableHead>
                  <TableHead>First AI activity</TableHead>
                  <TableHead>Purchased</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {new_accounts.map((account, idx) => (
                  <TableRow key={`${account.organization_id}-${idx}`}>
                    <TableCell>
                      <p className="font-medium">
                        {account.account_name || account.owner_email}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {account.owner_email}
                      </p>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(account.signup_at)}
                    </TableCell>
                    <TableCell>
                      {account.import_status === null ? (
                        <span className="text-muted-foreground">No import</span>
                      ) : (
                        <ImportBadge status={account.import_status} />
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {account.first_ai_at
                        ? formatDate(account.first_ai_at)
                        : "-"}
                    </TableCell>
                    <TableCell>{account.purchased ? "✓" : "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Recent payments */}
      <Card>
        <CardHeader>
          <CardTitle>Recent payments</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {recent_payments.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No payments yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Fakturoid</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent_payments.map((payment, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-muted-foreground">
                      {payment.paid_at ? formatDate(payment.paid_at) : "-"}
                    </TableCell>
                    <TableCell>{payment.email}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {payment.description || "-"}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(payment.amount, payment.currency)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          payment.status === "succeeded"
                            ? "success"
                            : "secondary"
                        }
                      >
                        {payment.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {payment.fakturoid_invoice_url ? (
                        <a
                          href={payment.fakturoid_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
